import { z } from "zod";
import { calculationAmountMultiplier } from "./monetary-basis.js";
const totalsSchema = z
    .object({
    ipi: z.string().regex(/^\d+\.\d{2}$/),
    icmsSt: z
        .string()
        .regex(/^\d+\.\d{2}$/)
        .nullable(),
    taxTotal: z.string().regex(/^\d+\.\d{2}$/),
    landed: z.string().regex(/^\d+\.\d{2}$/),
})
    .strict();
const groupSchema = z
    .object({
    key: z.string(),
    /** Every stored row of the group, review rows included. */
    rows: z.number().int().nonnegative(),
    /** Usable rows `totals` sums (CALCULATED, authoritative, no finance review); absent on older API responses. */
    calculatedRows: z.number().int().nonnegative().optional(),
    totals: totalsSchema.nullable(),
})
    .strict();
/** Exact sums of persisted LINE amounts; currency formatting does not change these values. */
const acquisitionQuoteAmountSchema = z
    .string()
    .regex(/^(?:0|[1-9]\d*)(?:\.\d+)?$/)
    .max(1400);
export const lotAcquisitionQuoteSubtotalSchema = z
    .object({
    count: z.number().int().nonnegative(),
    principal: acquisitionQuoteAmountSchema,
    ipi: acquisitionQuoteAmountSchema,
    icmsSt: acquisitionQuoteAmountSchema,
    fcp: acquisitionQuoteAmountSchema,
    acquisitionCost: acquisitionQuoteAmountSchema,
    listingPrice: acquisitionQuoteAmountSchema,
    takeAmount: acquisitionQuoteAmountSchema,
})
    .strict();
export const lotAcquisitionQuotesSchema = z
    .object({
    confirmed: lotAcquisitionQuoteSubtotalSchema,
    estimated: lotAcquisitionQuoteSubtotalSchema,
    combined: lotAcquisitionQuoteSubtotalSchema,
    /** Stored result rows without a complete confirmed or selected estimated LINE quote. */
    unpricedCount: z.number().int().nonnegative(),
})
    .strict()
    .refine((value) => value.combined.count === value.confirmed.count + value.estimated.count, {
    message: "Combined quote count must equal confirmed plus estimated rows",
    path: ["combined", "count"],
});
/** Read projection of completed, usable records. Values are quantity-weighted BRL. */
export const lotAnalysisSchema = z
    .object({
    resultCount: z.number().int().nonnegative(),
    calculatedCount: z.number().int().nonnegative(),
    /** Calculated rows carrying a non-blocking `coverage_needs_review` warning; absent on older API responses. */
    calculatedWithWarningCount: z.number().int().nonnegative().optional(),
    totals: totalsSchema.nullable(),
    byNcm: z.array(groupSchema),
    byRoute: z.array(groupSchema),
    statuses: z.array(z.string()),
    issues: z.array(z.string()),
    /** Server-owned exact publication quotes, separate from legacy fiscal totals. */
    acquisitionQuotes: lotAcquisitionQuotesSchema.optional(),
})
    .strict()
    .refine((value) => !value.acquisitionQuotes ||
    value.acquisitionQuotes.combined.count + value.acquisitionQuotes.unpricedCount ===
        value.resultCount, {
    message: "Every stored result must be priced or explicitly unpriced",
    path: ["acquisitionQuotes", "unpricedCount"],
});
const blank = () => ({
    rows: 0,
    usable: 0,
    ipi: 0n,
    icmsSt: 0n,
    unknownSt: false,
    taxTotal: 0n,
    landed: 0n,
});
const cents = (value) => {
    const [whole, fraction = ""] = value.split(".");
    return BigInt(whole ?? "0") * 100n + BigInt(fraction.padEnd(2, "0"));
};
const money = (value) => `${value / 100n}.${String(value % 100n).padStart(2, "0")}`;
const totals = (sum) => sum.usable
    ? {
        ipi: money(sum.ipi),
        icmsSt: sum.unknownSt ? null : money(sum.icmsSt),
        taxTotal: money(sum.taxTotal),
        landed: money(sum.landed),
    }
    : null;
/** Streaming accumulator: never sums unit values or placeholder zeros for review rows. */
export function createLotAnalysisAccumulator() {
    const sum = blank();
    const ncm = new Map();
    const route = new Map();
    const statuses = new Set();
    const issues = new Set();
    let warned = 0;
    function add(row) {
        const keys = [
            row.input.ncm ?? "—",
            `${row.input.originUf ?? "—"} → ${row.input.destinationUf ?? "—"}`,
        ];
        const groups = [ncm, route].map((map, index) => {
            const key = keys[index] ?? "—";
            const group = map.get(key) ?? blank();
            map.set(key, group);
            return group;
        });
        statuses.add(row.result.status);
        if (row.result.status === "CALCULATED" && row.warnings?.length)
            warned++;
        for (const issue of row.result.issues)
            issues.add(issue);
        for (const group of [sum, ...groups]) {
            group.rows++;
            if (row.result.status !== "CALCULATED" ||
                !row.result.authoritative ||
                row.result.financeReviewRequired)
                continue;
            group.usable++;
            const quantity = calculationAmountMultiplier(row.input, row.result);
            group.ipi += cents(row.result.amounts.ipi) * quantity;
            if (row.memory?.stAmount == null)
                group.unknownSt = true;
            else
                group.icmsSt += cents(row.memory.stAmount) * quantity;
            group.taxTotal += cents(row.result.amounts.taxTotal) * quantity;
            group.landed += cents(row.result.amounts.landedUnitCost) * quantity;
        }
    }
    const project = (map) => [...map]
        .map(([key, value]) => ({
        key,
        rows: value.rows,
        calculatedRows: value.usable,
        totals: totals(value),
    }))
        .sort((a, b) => Number(b.totals?.taxTotal ?? 0) - Number(a.totals?.taxTotal ?? 0));
    return {
        add,
        result: () => ({
            resultCount: sum.rows,
            calculatedCount: sum.usable,
            calculatedWithWarningCount: warned,
            totals: totals(sum),
            byNcm: project(ncm),
            byRoute: project(route),
            statuses: [...statuses],
            issues: [...issues],
        }),
    };
}
/** Classify the stored tape, never infer ST from the historical generic ICMS amount. */
export function lotTaxMemory(trace) {
    const st = trace.find((step) => step.code === "ICMS_ST_CALCULATED");
    const own = trace.find((step) => step.code === "ICMS_STANDARD_CALCULATED");
    const excluded = trace.some((step) => step.code === "TAX_EXCLUDED" && step.component === "ICMS");
    return {
        stBase: st?.inputs?.effectiveStBase ?? null,
        ownIcms: st?.inputs?.ownOperationIcms ?? own?.result ?? (excluded ? "0.00" : null),
        stAmount: st?.result ?? (own || excluded ? "0.00" : null),
    };
}
//# sourceMappingURL=lot-analysis.js.map